import { BaseCategory } from "./BaseCategory";
import { AuthoringBlockType, TextStyleType } from "../constants";
import { SlideCategories } from "./CategoryController";
import { SlideModelBuilder } from "./SlideModelBuilder";
import { v4 as uuid } from "uuid";

export class ContactInfoCategory extends BaseCategory {
    get name() {
        return "Contact Info";
    }

    get type() {
        return SlideCategories.CONTACT_INFO;
    }

    get icon() {
        return null;
    }

    get description() {
        return "Use for displaying contact information including company name, address, phone, email, website, and social media handles. Can optionally include a map or image.";
    }

    get slideTypes() {
        return [];
    }

    getPrompt() {
        return `Create a contact information slide based on the user's prompt.
        Extract contact details such as company/organization name, address, phone, email, website, and social media.
        Organize the information into appropriate contact items.`;
    }

    getPromptSchema() {
        return {
            contactHeader: "optional main header (company/organization name)",
            address: "optional full address for map display",
            contactItems: [{
                type: "phone | email | fax | website | social | text",
                text: "the contact information text (phone number, email, etc.)",
                socialType: "optional: if type is 'social', specify the platform (e.g., 'twitter', 'linkedin', 'facebook')"
            }],
            conclusion: "an optional short conclusion or takeaway for the slide",
        };
    }

    getImagePrompt() {
        return false;
    }

    getSlideModels(model) {
        let models = [];

        // Build variants based on available data
        if (model.address) {
            // If we have an address, create map variants
            models.push(this.buildContactInfoSlide(model, "left_map"));
            models.push(this.buildContactInfoSlide(model, "right_map"));
        }

        // If we have an image search, create image variants
        models.push(this.buildContactInfoSlide(model, "left_picture"));
        models.push(this.buildContactInfoSlide(model, "right_picture"));

        // Always add a text-only variant
        models.push(this.buildContactInfoSlide(model, "none"));

        return models;
    }

    buildContactInfoSlide(model, layout = "none") {
        let builder = new SlideModelBuilder("contactus", model);

        const items = [];

        // Add contact header if provided
        if (model.contactHeader) {
            items.push({
                id: uuid(),
                type: "contact_header",
                text: {
                    blocks: [
                        {
                            id: uuid(),
                            html: model.contactHeader,
                            textStyle: TextStyleType.TITLE,
                            type: AuthoringBlockType.TEXT
                        }
                    ]
                }
            });
        }

        // Add address if provided and layout uses map
        if (model.address && (layout === "left_map" || layout === "right_map")) {
            items.push({
                id: uuid(),
                type: "address"
            });
        }

        // Add contact items
        if (model.contactItems && model.contactItems.length > 0) {
            for (const contactItem of model.contactItems) {
                const item = {
                    id: uuid(),
                    type: contactItem.type || "text",
                    text: {
                        blocks: [
                            {
                                id: uuid(),
                                html: contactItem.text || "",
                                textStyle: TextStyleType.BODY,
                                type: AuthoringBlockType.TEXT
                            }
                        ]
                    }
                };

                // Add social type if specified
                if (contactItem.type === "social" && contactItem.socialType) {
                    item.social_type = contactItem.socialType;
                }

                items.push(item);
            }
        }

        // Create primary data
        const primaryData = {
            layout: layout,
            items: items,
        };

        // Add map address if we have an address and using map layout
        if (model.address && (layout === "left_map" || layout === "right_map")) {
            primaryData.map_address = {
                blocks: [
                    {
                        id: uuid(),
                        html: model.address,
                        textStyle: TextStyleType.TITLE,
                        type: AuthoringBlockType.TEXT
                    }
                ]
            };
        }

        // Add image if we have image search and using picture layout
        if (model.slideImage && (layout === "left_picture" || layout === "right_picture" || layout === "top_picture")) {
            Object.assign(primaryData, builder.createImage(model.slideImage));
        }

        builder.createPrimary(primaryData);

        if (model.conclusion) {
            builder.createBottomTray(model.conclusion);
        }

        return builder.model;
    }
}
